/**
 * Paths of this app, and the split view's old address (WA-2): before tabs (F90) the second
 * page was the query parameter `split`. Such links still open once, the second page beside
 * the first (`legacyLinks.ts`). Pure, so the rules are tested in one place.
 */
export const SPLIT_PARAMETER = "split";

/** Where an address points: `pathname`, `search` (with `?`) and `hash` (with `#`), as React Router's location. */
export interface AddressParts {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

/** An app path's three address parts, as React Router's location. */
export function pathParts(path: string): AddressParts {
  const url = new URL(path, "http://app.invalid");
  return { pathname: url.pathname, search: url.search, hash: url.hash };
}

/**
 * Whether `path` is a path of this app: it starts with one `/`, so it can name no other
 * site (`//host`, `/\host`) and no scheme, and holds no control characters.
 */
export function isAppPath(path: string): boolean {
  for (let index = 0; index < path.length; index++) {
    const code = path.charCodeAt(index);
    if (code < 0x20 || code === 0x7f) {
      return false;
    }
  }
  return path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\");
}

/** `path` without a `split` of its own: an old side page never opens a third one. */
export function withoutNestedSplit(path: string): string {
  const url = new URL(path, "http://app.invalid");
  url.searchParams.delete(SPLIT_PARAMETER);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The side page an old address names, or `null` when it names none or a path outside the app. */
export function sidePathOf(search: string): string | null {
  const path = new URLSearchParams(search).get(SPLIT_PARAMETER);
  return path !== null && isAppPath(path) ? withoutNestedSplit(path) : null;
}

/** The path part of an app path, without its query and fragment. */
export function pathnameOf(path: string): string {
  return new URL(path, "http://app.invalid").pathname;
}

/** The main page of an old address, without its side page. */
export function mainPathOf(address: AddressParts): string {
  const parameters = new URLSearchParams(address.search);
  parameters.delete(SPLIT_PARAMETER);
  const search = parameters.toString();
  return `${address.pathname}${search === "" ? "" : `?${search}`}${address.hash}`;
}
